import { FileBox, FileKey2, FileOutput, FileQuestionMark, FileText } from 'lucide-vue-next'

export const EQO_STATUSES = {
  ENQUIRY: 'enquiry',
  QUOTE: 'quote',
  ORDER: 'order',
  WARRANTY: 'warranty',
  RETURN: 'return',
}

export const EQO_CATEGORIES = {
  REPAIR: 'repair',
}

export const EQO_PART_SUPPLIER_STATUSES = [
  { label: 'Select' },
  { label: 'Emailed Supplier' },
  { label: 'PO Sent' },
  { label: 'Order Confirmation' },
  { label: 'Shipped by Supplier' },
  { label: 'Arrived at Warehouse' },
  { label: 'Ready to Ship' },
]

const PO_SENT = 'PO Sent'
const OC = 'Order Confirmation'
const SHIPPED = 'Shipped by Supplier'
const ARRIVED = 'Arrived at Warehouse'
const READY_TO_SHIP = 'Ready to Ship'

export const getEqoIcon = (status) => {
  switch (status) {
    case EQO_STATUSES.ENQUIRY:
      return FileQuestionMark
    case EQO_STATUSES.QUOTE:
      return FileText
    case EQO_STATUSES.ORDER:
      return FileBox
    case EQO_STATUSES.WARRANTY:
      return FileKey2
    case EQO_STATUSES.RETURN:
      return FileOutput
    default:
      return FileQuestionMark
  }
}

export const getEqoColor = (status, type = 'text') => {
  const colorMap = {
    [EQO_STATUSES.ENQUIRY]: 'blue-500',
    [EQO_STATUSES.QUOTE]: 'amber-500',
    [EQO_STATUSES.ORDER]: 'green-500',
    [EQO_STATUSES.WARRANTY]: 'purple-500',
    [EQO_STATUSES.RETURN]: 'rose-500',
  }

  const color = colorMap[status] || 'gray-400'
  return `${type}-${color}`
}

export function buildEurRateMap(currencies = []) {
  const map = Object.fromEntries(
    (currencies || [])
      .filter((c) => String(c.base).toUpperCase() === 'EUR')
      .map((c) => [String(c.code).toUpperCase(), Number(c.rate)])
  )
  map.EUR = map.EUR ?? 1
  return map
}

function priceToEur(price, fromCode, eurRateMap) {
  const p = Number(price)
  if (!Number.isFinite(p)) return null

  const code = String(fromCode || 'EUR').toUpperCase()
  const rate = eurRateMap[code]
  if (!rate) return null

  return p * rate
}

function eurToPrice(amountEur, toCode, eurRateMap) {
  const a = Number(amountEur)
  if (!Number.isFinite(a)) return null

  const code = String(toCode || 'EUR').toUpperCase()
  const rate = eurRateMap[code]
  if (!rate) return null

  return a / rate
}

function convertPrice(price, fromCode, toCode, eurRateMap) {
  if (!eurRateMap) return null
  const eur = priceToEur(price, fromCode, eurRateMap)
  if (eur == null) return null
  return eurToPrice(eur, toCode, eurRateMap)
}

const isHiddenFlag = (obj) => obj?.isHidden === true || obj?.is_hidden === true

function getCurrency(obj, fallback = { code: 'EUR', symbol: '€' }) {
  const c = obj?.currency
  const code = String(c?.code ?? fallback.code ?? 'EUR').toUpperCase()
  const symbol = c?.symbol ?? fallback.symbol ?? (code === 'EUR' ? '€' : code)
  return { code, symbol }
}

const round2 = (n) => Math.round(Number(n) * 100) / 100

export function calcSupplierTotals(eqo, eurRateMap) {
  if (!eqo) return []

  const parts = Array.isArray(eqo.parts) ? eqo.parts.filter((p) => !isHiddenFlag(p)) : []
  const purchaseMap = buildSupplierPurchaseMap(eqo)
  const orderIdx = buildSupplierOrderIndex(eqo)

  const map = new Map()

  for (const part of parts) {
    const suppliers = Array.isArray(part?.partSuppliers) ? part.partSuppliers : []

    for (const ps of suppliers) {
      if (isHiddenFlag(ps)) continue

      const supplier = ps?.supplier
      const supplierId = Number(supplier?.id)
      if (!Number.isFinite(supplierId) || supplierId <= 0) continue

      const supplierCur = supplier?.currency ? getCurrency(supplier) : getCurrency(ps)

      const qty = Number(ps?.qty ?? ps?.quantity ?? 1)
      if (!Number.isFinite(qty) || qty <= 0) continue

      const price = Number(ps?.price ?? 0)
      if (!Number.isFinite(price)) continue

      const priceCur = ps?.currency ? getCurrency(ps) : supplierCur

      const lineTotalInPriceCur = price * qty
      const lineTotalSupplierCur =
        priceCur.code === supplierCur.code
          ? lineTotalInPriceCur
          : convertPrice(lineTotalInPriceCur, priceCur.code, supplierCur.code, eurRateMap)

      if (lineTotalSupplierCur == null) continue

      const row =
        map.get(supplierId) ??
        (() => {
          const purchase = purchaseMap.get(supplierId) ?? null

          return {
            supplierId,
            supplierName: supplier?.companyName ?? supplier?.gmgName ?? supplier?.number ?? `Supplier #${supplierId}`,
            supplierPaymentTerm: supplier?.paymentTerm?.name ?? null,
            currencyCode: supplierCur.code,
            currencySymbol: supplierCur.symbol,

            parts: 0,
            amount: 0,

            costs: Number(purchase?.costs ?? 0),
            shipping: Number(purchase?.shipping ?? 0),
            vatAmount: Number(purchase?.vatAmount ?? purchase?.vat_amount ?? 0),
            paymentDate: purchase?.paymentDate ?? purchase?.payment_date ?? null,
            paymentMethod: purchase?.paymentMethod ?? purchase?.payment_method ?? null,

            attachments: purchase?.attachments ?? { invoice: [], 'credit note': [], file: [] },

            totalSupplier: 0,
            totalEur: 0,

            poTotalCount: 0,
            poCount: 0,
            ocTotalCount: 0,
            ocCount: 0,
            shippedTotalCount: 0,
            shippedCount: 0,
          }
        })()

      row.parts += 1
      row.amount += lineTotalSupplierCur

      const status = String(ps?.status ?? '').trim()

      row.poTotalCount += 1
      if ([SHIPPED, ARRIVED, READY_TO_SHIP, OC, PO_SENT].includes(status)) row.poCount += 1

      row.ocTotalCount += 1
      if ([SHIPPED, ARRIVED, READY_TO_SHIP, OC].includes(status)) row.ocCount += 1

      row.shippedTotalCount += 1
      if ([SHIPPED, ARRIVED, READY_TO_SHIP].includes(status)) row.shippedCount += 1

      map.set(supplierId, row)
    }
  }

  return Array.from(map.values())
    .map((r) => {
      const amount = round2(r.amount)
      const costs = round2(r.costs ?? 0)
      const shipping = round2(r.shipping ?? 0)
      const vatAmount = round2(r.vatAmount ?? 0)

      const totalSupplier = round2(amount + costs + shipping + vatAmount)
      const totalEur = convertPrice(totalSupplier, r.currencyCode, 'EUR', eurRateMap)

      return {
        ...r,
        amount,
        costs,
        shipping,
        vatAmount,
        totalSupplier,
        totalEur: round2(totalEur ?? 0),
      }
    })
    .sort((a, b) => {
      const ai = orderIdx.has(a.supplierId) ? orderIdx.get(a.supplierId) : Number.MAX_SAFE_INTEGER
      const bi = orderIdx.has(b.supplierId) ? orderIdx.get(b.supplierId) : Number.MAX_SAFE_INTEGER

      if (ai !== bi) return ai - bi
      return a.supplierId - b.supplierId
    })
}

const normalizeAttachments = (a) => ({
  invoice: Array.isArray(a?.invoice) ? a.invoice : [],
  'credit note': Array.isArray(a?.['credit note']) ? a['credit note'] : [],
  file: Array.isArray(a?.file) ? a.file : [],
})

function normalizePurchase(p) {
  return {
    ...p,

    supplierId: Number(p?.supplierId ?? p?.supplier_id ?? 0) || null,

    costs: Number(p?.costs ?? 0) || 0,
    shipping: Number(p?.shipping ?? 0) || 0,
    vatAmount: Number(p?.vatAmount ?? p?.vat_amount ?? 0) || 0,

    paymentDate: p?.paymentDate ?? p?.payment_date ?? null,
    paymentMethod: p?.paymentMethod ?? p?.payment_method ?? null,

    attachments: normalizeAttachments(p?.attachments),
  }
}

function buildSupplierOrderIndex(eqo) {
  const list = Array.isArray(eqo?.supplierPurchases) ? eqo.supplierPurchases : []
  const idx = new Map()
  let i = 0

  for (const p of list) {
    const supplierId = Number(p?.supplierId ?? p?.supplier_id ?? 0)
    if (!Number.isFinite(supplierId) || supplierId <= 0) continue
    if (!idx.has(supplierId)) idx.set(supplierId, i++)
  }

  return idx
}

function buildSupplierPurchaseMap(eqo) {
  const list = Array.isArray(eqo?.supplierPurchases) ? eqo.supplierPurchases : []

  const map = new Map()
  for (const raw of list) {
    const p = normalizePurchase(raw)

    const supplierId = Number(p?.supplierId)
    if (!Number.isFinite(supplierId) || supplierId <= 0) continue

    map.set(supplierId, p)
  }

  return map
}

export function calcPurchasingAdditionalCostsEUR(eqo) {
  if (!eqo) return 0

  const list = Array.isArray(eqo?.purchasingAdditionalCosts) ? eqo.purchasingAdditionalCosts : []
  if (!list.length) return 0

  const toNum = (v) => {
    const n = Number(v)
    return Number.isFinite(n) ? n : 0
  }

  let sum = 0
  for (const c of list) {
    sum += toNum(c?.amount) + toNum(c?.shipping)
  }

  return Math.round(sum * 100) / 100
}
